import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, CheckCircle, Award, Users, Building2,
  Repeat, DollarSign, BarChart3, Shield, Target, UserPlus
} from 'lucide-react';

const useCases = [
  { icon: UserPlus, title: "Agent onboarding", desc: "Recommend AREA to new agents joining your brokerage. Every paid membership earns you recurring commissions." },
  { icon: Users, title: "Team referrals", desc: "Refer entire teams at once. Each member who upgrades to Pro or Founder locks your attribution for life." },
  { icon: Building2, title: "Multi-office expansion", desc: "Scale referrals across offices and regions. Track performance by campaign and source." },
  { icon: Target, title: "Training programs", desc: "Integrate AREA into your onboarding or training curriculum as a recommended career resource." },
];

const earnings = [
  { label: "25 Pro agents/year", value: "$618.75", sub: "25% of $99 x 25 (recurring annually)" },
  { label: "5 Founder members", value: "$1,498.50", sub: "30% of $999 x 5 (one-time)" },
  { label: "100-agent brokerage", value: "$2,475/yr", sub: "25% of $99 x 100 (recurring)" },
];

export default function AffiliateBrokerages() {
  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-brokerages">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/area-logo.png" alt="AREA" className="h-10" />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <Link to="/affiliate" className="hover:text-[#1e3a5f] transition-colors">Affiliate</Link>
            <Link to="/affiliate/influencers" className="hover:text-[#1e3a5f] transition-colors">For Creators</Link>
            <Link to="/affiliate/brokerages" className="text-[#1e3a5f] font-semibold">For Brokerages</Link>
            <Link to="/login" className="hover:text-[#1e3a5f] transition-colors">Sign In</Link>
          </div>
          <Link to="/register"><Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6">Join AREA</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f0f7ff] to-white"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold mb-8">
            <Building2 className="w-4 h-4" /> Brokerage Partnerships
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0f1f33] leading-tight tracking-tight mb-6" data-testid="brokerage-hero-title">
            A partner program for brokerages and teams
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed mb-10">
            Refer agents and teams into AREA and create a long-term revenue stream with transparent tracking and lifetime commissions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/affiliate/apply">
              <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold shadow-lg">
                Partner With AREA <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/affiliate">
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 px-8 py-6 text-lg">View Full Program</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">Who This Is For</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Built for broker-owners, team leads, and managing brokers</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {useCases.map((u, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <u.icon className="w-6 h-6 text-[#2563eb]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f33] mb-2">{u.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">Revenue Potential</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Scale your partner revenue with your roster</h2>
          </div>
          <div className="space-y-4">
            {earnings.map((e, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div>
                  <p className="font-semibold text-[#0f1f33]">{e.label}</p>
                  <p className="text-sm text-gray-400">{e.sub}</p>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{e.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Admin control and transparency</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: BarChart3, text: "Real-time dashboard with click, signup, and earnings data" },
              { icon: Repeat, text: "Lifetime recurring commissions on all referred members" },
              { icon: Shield, text: "Transparent attribution — see exactly who you referred" },
              { icon: DollarSign, text: "Direct deposit payouts with clear threshold and payout history" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#2563eb]" />
                </div>
                <p className="text-[#0f1f33] font-medium text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0f1f33]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Partner with AREA today</h2>
          <p className="text-lg text-gray-300 mb-10">Start referring your agents and teams. Build a recurring revenue stream alongside your brokerage.</p>
          <Link to="/affiliate/apply">
            <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-10 py-6 text-lg font-semibold shadow-lg">
              Apply as a Brokerage Partner <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white border-t border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <img src="/images/area-logo.png" alt="AREA" className="h-6" />
          <div className="flex gap-6">
            <Link to="/affiliate/terms" className="hover:text-gray-600">Affiliate Terms</Link>
            <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
          </div>
          <span>&copy; {new Date().getFullYear()} American Real Estate Alliance</span>
        </div>
      </footer>
    </div>
  );
}
